import React, { useState, useRef, useEffect } from "react";
import Layout from "@/components/layout/Layout";
import PageHero from "@/components/ui/PageHero";
import { Helmet } from "react-helmet-async";
import { Truck, Search, Loader2, Info, ChevronDown, Send } from "lucide-react";
import { STATIC_ASSETS } from "@/lib/imageUtils";
import { YEARS, MAKES_MODELS, CAB_TYPES, AXLE_TYPES } from "@/data/towVehicleData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { getSupabase } from "@/integrations/supabase/lazy";
import RVCard from "@/components/inventory/RVCard";
import type { RVUnit } from "@/hooks/useInventory";
import { toast } from "sonner";

const CHAT_URL = `${import.meta.env['VITE_SUPABASE_URL']}/functions/v1/tow-guide`;

const DEFINITIONS = [
  { term: "Dry Weight (UVW)", desc: "The weight of the RV as shipped from the factory, without any cargo, water, propane, or passengers." },
  { term: "Gross Vehicle Weight (GVW)", desc: "The actual total weight of the RV when fully loaded with cargo, water, fuel, and passengers." },
  { term: "Gross Vehicle Weight Rating (GVWR)", desc: "The maximum allowable weight of the RV when fully loaded. Never exceed this number." },
  { term: "Cargo Weight (CCC)", desc: "The maximum weight of personal belongings, food, water, and supplies you can add to the RV." },
  { term: "Tongue / Hitch Weight", desc: "The downward force the trailer exerts on the tow vehicle's hitch. Typically 10–15% of the trailer's total weight." },
  { term: "Pin Weight", desc: "Similar to tongue weight but for fifth-wheel trailers. The weight at the kingpin connection point in the truck bed." },
  { term: "Payload Capacity", desc: "The maximum weight your tow vehicle can carry in and on it, including passengers, cargo, AND tongue/pin weight." },
  { term: "Max Towing Capacity", desc: "The maximum weight your vehicle is rated to tow. Always stay at or below 80% of this number for a safe margin." },
];

async function streamChat(
  messages: { role: string; content: string }[],
  onDelta: (t: string) => void,
  onDone: () => void,
) {
  const resp = await fetch(CHAT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${import.meta.env['VITE_SUPABASE_PUBLISHABLE_KEY']}`,
    },
    body: JSON.stringify({ messages }),
  });

  if (!resp.ok || !resp.body) {
    const err = await resp.json().catch(() => ({ error: "Failed" }));
    throw new Error(err.error || "Stream failed");
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done = false;

  while (!done) {
    const { done: d, value } = await reader.read();
    if (d) break;
    buf += decoder.decode(value, { stream: true });

    let idx: number;
    while ((idx = buf.indexOf("\n")) !== -1) {
      let line = buf.slice(0, idx);
      buf = buf.slice(idx + 1);
      if (line.endsWith("\r")) line = line.slice(0, -1);
      if (!line.startsWith("data: ")) continue;
      const json = line.slice(6).trim();
      if (json === "[DONE]") { done = true; break; }
      try {
        const p = JSON.parse(json);
        const c = p.choices?.[0]?.delta?.content;
        if (c) onDelta(c);
      } catch {
        buf = line + "\n" + buf;
        break;
      }
    }
  }
  onDone();
}

const TowGuidePage = () => {
  const [year, setYear] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [cabType, setCabType] = useState("");
  const [engineSize, setEngineSize] = useState("");
  const [axleType, setAxleType] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState("");
  const [matchedSlugs, setMatchedSlugs] = useState<string[]>([]);
  const [matchedUnits, setMatchedUnits] = useState<RVUnit[]>([]);
  const [viewAllUrl, setViewAllUrl] = useState<string>("");
  const [chatHistory, setChatHistory] = useState<{ role: string; content: string }[]>([]);
  const [followUp, setFollowUp] = useState("");
  const resultRef = useRef<HTMLDivElement>(null);
  const followUpRef = useRef<HTMLInputElement>(null);

  const makes = Object.keys(MAKES_MODELS).sort();
  const models = make ? MAKES_MODELS[make] ?? [] : [];

  const handleMakeChange = (val: string) => {
    setMake(val);
    setModel("");
  };

  // Fetch matched units when slugs change
  useEffect(() => {
    if (matchedSlugs.length === 0) { setMatchedUnits([]); return; }
    (async () => {
      const { data } = await (await getSupabase())
        .from("rv_inventory")
        .select("*")
        .in("slug", matchedSlugs)
        .in("status", ["available", "pending", "coming_soon"]);
      if (data) setMatchedUnits(data as unknown as RVUnit[]);
    })();
  }, [matchedSlugs]);

  const runStream = async (messages: { role: string; content: string }[]) => {
    setLoading(true);
    setError("");
    let accumulated = "";

    try {
      await streamChat(
        messages,
        (chunk) => {
          accumulated += chunk;
          setResult((prev) => prev + chunk);
        },
        () => {
          setLoading(false);
          // Parse slugs from all accumulated result text
          const fullText = result + accumulated;
          const slugRegex = /\/(?:inventory|product)\/([\w-]+)/g;
          const slugs = Array.from(fullText.matchAll(slugRegex), m => m[1]).filter((s): s is string => !!s);
          if (slugs.length > 0) setMatchedSlugs([...new Set(slugs)]);
          // Parse weight_max for "View All" CTA
          const weightMatch = fullText.match(/weight_max=(\d+)/);
          if (weightMatch) setViewAllUrl(`/rv-search?weight_max=${weightMatch[1]}`);
          // Update chat history with assistant response
          setChatHistory((prev) => [...prev, { role: "assistant", content: accumulated }]);
          setTimeout(() => followUpRef.current?.focus(), 300);
        },
      );
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    } catch (e: any) {
      setError(e.message || "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!year || !make || !model) return;
    setResult("");
    setMatchedSlugs([]);
    setViewAllUrl("");
    setChatHistory([]);

    let vehicleDesc = `${year} ${make} ${model}`;
    if (cabType) vehicleDesc += ` ${cabType}`;
    if (engineSize.trim()) vehicleDesc += ` with a ${engineSize.trim()}`;
    if (axleType) vehicleDesc += `, ${axleType}`;

    const userMsg = { role: "user", content: `I have a ${vehicleDesc}. What RVs from your inventory can I safely tow?` };
    setChatHistory([userMsg]);
    await runStream([userMsg]);
  };

  const handleFollowUp = async () => {
    if (!followUp.trim() || loading) return;
    const userMsg = { role: "user", content: followUp.trim() };
    const updatedHistory = [...chatHistory, userMsg];
    setChatHistory(updatedHistory);
    setFollowUp("");
    // Append a separator in the result display
    setResult((prev) => prev + "\n\n---\n\n**You:** " + userMsg.content + "\n\n");
    await runStream(updatedHistory);
  };

  const extractText = (node: React.ReactNode): string => {
    if (node == null || typeof node === "boolean") return "";
    if (typeof node === "string" || typeof node === "number") return String(node);
    if (Array.isArray(node)) return node.map(extractText).join("");
    if (React.isValidElement(node)) return extractText((node.props as any).children);
    return "";
  };

  const handleRowSelect = async (cells: string[], headers: string[]) => {
    if (loading || cells.length === 0) return;
    const parts = cells.map((c, i) => {
      const h = headers[i]?.trim();
      return h ? `${h}: ${c.trim()}` : c.trim();
    });
    const content = `I have this exact configuration — ${parts.join(", ")}. Based on this, what RVs from your inventory can I safely tow? Please list specific units with links.`;
    const userMsg = { role: "user", content };
    const updatedHistory = [...chatHistory, userMsg];
    setChatHistory(updatedHistory);
    setResult((prev) => prev + "\n\n---\n\n**You selected:** " + parts.join(" • ") + "\n\n");
    await runStream(updatedHistory);
  };

  return (
    <Layout>
      <Helmet>
        <title>RV Tow Guide – Find What You Can Tow | Rex & Sons RVs</title>
      </Helmet>

      <PageHero
        title="RV Tow Guide"
        subtitle="Find the perfect RV your vehicle can safely tow"
        backgroundImage={STATIC_ASSETS.heroInventory1920}
        backgroundImageMobile={STATIC_ASSETS.heroInventory640}
      />

      {/* Vehicle Selector */}
      <section className="bg-[hsl(var(--primary))] py-8">
        <div className="container">
          <h2 className="text-center font-heading text-xl font-bold uppercase text-primary-foreground mb-6">
            Find My Vehicle's Towing Capacity
          </h2>

          <div className="mx-auto flex max-w-3xl flex-col items-center gap-4 sm:flex-row">
            {/* Year */}
            <div className="relative w-full sm:w-auto sm:flex-1">
              <select
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className="w-full appearance-none rounded-lg border-0 bg-primary-foreground px-4 py-3 pr-10 text-sm font-medium text-foreground shadow-xs focus:ring-2 focus:ring-accent"
              >
                <option value="">Year</option>
                {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            </div>

            {/* Make */}
            <div className="relative w-full sm:w-auto sm:flex-1">
              <select
                value={make}
                onChange={(e) => handleMakeChange(e.target.value)}
                className="w-full appearance-none rounded-lg border-0 bg-primary-foreground px-4 py-3 pr-10 text-sm font-medium text-foreground shadow-xs focus:ring-2 focus:ring-accent"
              >
                <option value="">Make</option>
                {makes.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            </div>

            {/* Model */}
            <div className="relative w-full sm:w-auto sm:flex-1">
              <select
                value={model}
                onChange={(e) => setModel(e.target.value)}
                disabled={!make}
                className="w-full appearance-none rounded-lg border-0 bg-primary-foreground px-4 py-3 pr-10 text-sm font-medium text-foreground shadow-xs focus:ring-2 focus:ring-accent disabled:opacity-50"
              >
                <option value="">Model / Trim</option>
                {models.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            </div>

            <Button
              onClick={handleSearch}
              disabled={!year || !make || !model || loading}
              size="lg"
              className="w-full sm:w-auto whitespace-nowrap bg-accent text-accent-foreground hover:bg-accent/90 font-heading uppercase"
            >
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
              Find RVs I Can Tow
            </Button>
          </div>

          {/* Optional: Cab Type, Engine Size, Axle Type */}
          <div className="mx-auto flex max-w-3xl flex-col items-center gap-4 sm:flex-row mt-4">
            <div className="relative w-full sm:w-auto sm:flex-1">
              <select
                value={cabType}
                onChange={(e) => setCabType(e.target.value)}
                className="w-full appearance-none rounded-lg border-0 bg-primary-foreground px-4 py-3 pr-10 text-sm font-medium text-foreground shadow-xs focus:ring-2 focus:ring-accent"
              >
                <option value="">Cab Type (optional)</option>
                {CAB_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            </div>

            <div className="w-full sm:w-auto sm:flex-1">
              <Input
                value={engineSize}
                onChange={(e) => setEngineSize(e.target.value)}
                placeholder="Engine Size, e.g. 5.7L V8"
                className="h-[46px] rounded-lg border-0 bg-primary-foreground text-sm font-medium text-foreground shadow-xs focus:ring-2 focus:ring-accent placeholder:text-muted-foreground"
              />
            </div>

            <div className="relative w-full sm:w-auto sm:flex-1">
              <select
                value={axleType}
                onChange={(e) => setAxleType(e.target.value)}
                className="w-full appearance-none rounded-lg border-0 bg-primary-foreground px-4 py-3 pr-10 text-sm font-medium text-foreground shadow-xs focus:ring-2 focus:ring-accent"
              >
                <option value="">Axle / Drivetrain (optional)</option>
                {AXLE_TYPES.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="border-b bg-muted/50 py-4">
        <div className="container">
          <p className="mx-auto max-w-3xl text-center text-sm italic text-muted-foreground">
            <Info className="mr-1 inline h-4 w-4 align-text-bottom" />
            <strong>Attention:</strong> Cargo weight reduces towing capacity. Always deduct the weight of passengers, fuel, and cargo in your tow vehicle from its max tow rating before comparing to RV weights. We apply an 80% safety margin to all recommendations.
          </p>
        </div>
      </section>

      {/* Results */}
      {(result || error || loading) && (
        <section ref={resultRef} className="py-10">
          <div className="container">
            <div className="mx-auto max-w-3xl rounded-xl border bg-card p-6 shadow-xs">
              <div className="flex items-center gap-2 mb-2">
                <Truck className="h-5 w-5 text-primary" />
                <h3 className="font-heading text-lg font-bold uppercase text-foreground">
                  Results for {year} {make} {model}
                </h3>
              </div>
              <p className="text-xs text-muted-foreground mb-4 italic">
                💡 Tip: Click the <strong>Select →</strong> button on any row to instantly use that exact configuration.
              </p>

              {error ? (
                <p className="text-destructive">{error}</p>
              ) : (
                <div className="prose prose-sm max-w-none text-foreground prose-headings:text-foreground prose-a:text-primary prose-strong:text-foreground">
                  {(() => {
                    const headerCtx: { current: string[] } = { current: [] };
                    return (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h3: ({ children }) => (
                            <h3 className="font-heading text-lg font-bold uppercase text-foreground text-center mt-6 mb-3">{children}</h3>
                          ),
                          table: ({ children }: any) => (
                            <div className="my-4 overflow-x-auto rounded-lg border border-border">
                              <table className="w-full border-collapse text-sm">{children}</table>
                            </div>
                          ),
                          thead: ({ children }) => (
                            <thead className="bg-primary text-primary-foreground">{children}</thead>
                          ),
                          th: ({ children }) => {
                            const text = extractText(children).trim();
                            if (text) headerCtx.current = [...headerCtx.current, text];
                            return (
                              <th className="px-3 py-2 text-left font-heading uppercase text-xs tracking-wide border-r border-primary-foreground/20 last:border-r-0">{children}</th>
                            );
                          },
                          tr: ({ children, node }: any) => {
                            const childArr = React.Children.toArray(children);
                            const isHeaderRow = node?.children?.some((c: any) => c.tagName === "th");
                            if (isHeaderRow) {
                              headerCtx.current = [];
                              return (
                                <tr className="border-b border-border">
                                  {children}
                                  <th className="px-3 py-2 text-left font-heading uppercase text-xs tracking-wide w-28 bg-primary text-primary-foreground">Action</th>
                                </tr>
                              );
                            }
                            const cells: string[] = childArr.map((c: any) => extractText(c?.props?.children).trim());
                            const headers = headerCtx.current;
                            const handleClick = (e: React.MouseEvent) => {
                              e.stopPropagation();
                              if (loading || cells.length === 0) return;
                              toast.success("Configuration selected", { description: "Finding RVs you can tow…" });
                              handleRowSelect(cells, headers);
                            };
                            return (
                              <tr className="border-b border-border last:border-b-0 even:bg-muted/40 hover:bg-primary/10 transition-colors">
                                {children}
                                <td className="px-3 py-2 border-l border-border align-middle text-center">
                                  <Button
                                    size="sm"
                                    disabled={loading}
                                    onClick={handleClick}
                                    className="bg-accent text-accent-foreground hover:bg-accent/90 font-heading uppercase text-xs h-8 px-3"
                                  >
                                    Select →
                                  </Button>
                                </td>
                              </tr>
                            );
                          },
                          td: ({ children }) => (
                            <td className="px-3 py-2 border-r border-border last:border-r-0 align-top">{children}</td>
                          ),
                        }}
                      >{result}</ReactMarkdown>
                    );
                  })()}
                  {loading && !result && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Analyzing your vehicle's towing specs…</span>
                    </div>
                  )}
                </div>
              )}

              {/* Follow-up input */}
              {!loading && result && !error && (
                <div className="mt-4 flex items-center gap-2 border-t pt-4">
                  <Input
                    ref={followUpRef}
                    value={followUp}
                    onChange={(e) => setFollowUp(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleFollowUp();
                      }
                    }}
                    placeholder="Ask a follow-up question (e.g. engine size, trim level)…"
                    className="flex-1"
                    disabled={loading}
                  />
                  <Button
                    size="icon"
                    onClick={handleFollowUp}
                    disabled={!followUp.trim() || loading}
                    className="shrink-0"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Matched Inventory Cards (with placeholder) */}
      <section className="py-10 bg-muted/30">
        <div className="container">
          <h2 className="font-heading text-xl font-bold uppercase text-foreground text-center mb-2">
            Towable Units From Our Inventory
          </h2>
          {viewAllUrl && (
            <div className="text-center mb-6">
              <Button asChild variant="default" className="bg-accent text-accent-foreground hover:bg-accent/90 font-heading uppercase">
                <a href={viewAllUrl}>👉 View All Possible Towable Units</a>
              </Button>
            </div>
          )}
          {matchedUnits.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center">
              {matchedUnits.map((unit) => (
                <RVCard key={unit.id} rv={unit} />
              ))}
            </div>
          ) : (
            <div className="mx-auto max-w-2xl mt-4 rounded-xl border border-dashed bg-card/50 p-8 text-center">
              <Truck className="mx-auto h-10 w-10 text-muted-foreground/60 mb-3" />
              <p className="text-sm text-muted-foreground">
                Matching RVs from our inventory will appear here once you confirm your truck's exact configuration in the chat above.
              </p>
            </div>
          )}
        </div>
      </section>

      <section className="py-16">
        <div className="container">
          <div className="mx-auto max-w-3xl">
            <h2 className="font-heading text-2xl font-bold uppercase text-foreground text-center mb-8">
              Terms &amp; Definitions
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {DEFINITIONS.map((d) => (
                <div key={d.term} className="rounded-xl border bg-card p-5 shadow-xs">
                  <h3 className="font-heading text-sm font-bold uppercase text-primary">{d.term}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{d.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TowGuidePage;
